import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { listMySubscriptions, cancelSubscription, renewSubscription, changeSubscriptionTier, subscribeToCreator, type UserSubscription } from '../../api/subscriptions'
import { getCreatorSubscriptionSettingsPublic, type CreatorSubscriptionSettingsPublic } from '../../api/creatorSubscriptions'
import { fetchPeopleReaderByUserId, type PeopleReader } from '../../api/peopleReader'
import { newIdempotencyKey } from '../../lib/idempotency'

type Row = {
  sub: UserSubscription
  people?: PeopleReader | null
  creatorSettings?: CreatorSubscriptionSettingsPublic | null
}

function toIsoShort(iso?: string | null) {
  if (!iso) return null
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return null
  }
}

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null
  try {
    const end = new Date(iso)
    if (Number.isNaN(end.getTime())) return null
    const ms = end.getTime() - Date.now()
    return ms / (1000 * 60 * 60 * 24)
  } catch {
    return null
  }
}

function StatusPill({ status }: { status: Row['sub']['status'] }) {
  const cfg =
    status === 'active'
      ? { label: 'Active', cls: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' }
      : status === 'canceled'
        ? { label: 'Canceled', cls: 'border-white/10 bg-white/5 text-white/70' }
        : status === 'paused'
          ? { label: 'Paused', cls: 'border-amber-400/30 bg-amber-500/10 text-amber-100' }
          : { label: 'Past due', cls: 'border-red-400/30 bg-red-500/10 text-red-100' }
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cfg.cls}`}>{cfg.label}</span>
}

export default function SubscriptionsManage() {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [confirm, setConfirm] = useState<{ creatorId: number; displayName: string } | null>(null)
  const [tierModal, setTierModal] = useState<{
    creatorId: number
    creatorName: string
    currentRank: number
    tiers: Array<{ id: number; name: string; rank: number; monthly_price_pixe: number }>
  } | null>(null)
  const [tierPick, setTierPick] = useState<number | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async (mode: 'init' | 'refresh') => {
    mode === 'init' ? setLoading(true) : setRefreshing(true)
    setError(null)
    try {
      const res = await listMySubscriptions()
      const subs = res?.results || []

      // Hydrate minimal creator info (public) in parallel.
      const hydrated: Row[] = await Promise.all(
        subs.map(async (sub) => {
          const creatorId = sub.creator_id
          const [people, creatorSettings] = await Promise.all([
            fetchPeopleReaderByUserId(creatorId).catch(() => null),
            getCreatorSubscriptionSettingsPublic(creatorId).catch(() => null),
          ])
          return { sub, people, creatorSettings }
        })
      )

      setRows(hydrated)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load subscriptions')
      setRows([])
    } finally {
      mode === 'init' ? setLoading(false) : setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load('init')
  }, [load])

  const active = useMemo(() => (rows || []).filter((r) => r.sub.status === 'active'), [rows])
  const inactive = useMemo(() => (rows || []).filter((r) => r.sub.status !== 'active'), [rows])

  async function doCancel(creatorId: number) {
    setActionError(null)
    setBusy(creatorId)
    try {
      await cancelSubscription(creatorId)
      // Optimistic update, then refresh to sync periods/status.
      setRows((prev) => (prev || []).map((r) => (r.sub.creator_id === creatorId ? { ...r, sub: { ...r.sub, status: 'canceled', renews: false } } : r)))
      await load('refresh')
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Cancel failed')
    } finally {
      setBusy(null)
    }
  }

  async function doResubscribe(creatorId: number) {
    setActionError(null)
    setBusy(creatorId)
    try {
      await subscribeToCreator(creatorId, { idempotencyKey: newIdempotencyKey('sub') })
      await load('refresh')
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Subscribe failed')
    } finally {
      setBusy(null)
    }
  }

  async function doRenew(creatorId: number) {
    setActionError(null)
    setBusy(creatorId)
    try {
      await renewSubscription(creatorId)
      await load('refresh')
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Renew failed')
    } finally {
      setBusy(null)
    }
  }

  async function doChangeTier(creatorId: number, tierId: number, changeType: 'upgrade' | 'downgrade') {
    setActionError(null)
    setBusy(creatorId)
    try {
      await changeSubscriptionTier(creatorId, { tier_id: tierId, change_type: changeType })
      await load('refresh')
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Change tier failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="text-white">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xl font-extrabold tracking-tight">My subscriptions</div>
            <div className="mt-1 text-sm text-white/70">
              Manage the creators you support. Cancel anytime.
            </div>
          </div>
          <button
            type="button"
            onClick={() => load('refresh')}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
            title="Refresh"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            <div className="flex items-center justify-between gap-3">
              <div>{error}</div>
              <button
                type="button"
                onClick={() => load('refresh')}
                className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {actionError && (
          <div className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {actionError}
          </div>
        )}

        {loading && (
          <div className="mt-6 grid grid-cols-1 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-20 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && (rows?.length || 0) === 0 && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-6">
            <div className="text-lg font-bold">You’re not subscribed to anyone yet.</div>
            <div className="mt-1 text-sm text-white/70">Explore creators and subscribe to unlock their drops in Subscriptions mode.</div>
            <div className="mt-4">
              <Link
                to="/people"
                className="inline-flex items-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                Explore creators
              </Link>
            </div>
          </div>
        )}

        {!loading && (rows?.length || 0) > 0 && (
          <div className="mt-6 space-y-6">
            <section>
              <div className="text-sm font-semibold text-white/85">Active</div>
              <div className="mt-3 space-y-3">
                {active.length === 0 ? (
                  <div className="text-sm text-white/60">No active subscriptions.</div>
                ) : (
                  active.map((r) => {
                    const creatorId = r.sub.creator_id
                    const hero = r.people?.hero
                    const publicId = r.people?._meta?.public_id
                    const href = publicId ? `/people/i/${encodeURIComponent(publicId)}` : '/people'
                    const price = r.creatorSettings?.monthly_price_pixe
                    const periodEnd = toIsoShort(r.sub.current_period_end)
                    const daysLeft = daysUntil(r.sub.current_period_end)
                    const renewEligible = daysLeft === null ? true : daysLeft <= 7
                    const renewalsEnabled = r.creatorSettings?.is_enabled !== false
                    const tierName = r.sub.tier_name || (r.sub.tier_rank ? `Tier ${r.sub.tier_rank}+` : null)
                    const tiers = (r.creatorSettings?.tiers || []).filter((t: any) => t?.is_active)
                    return (
                      <div key={creatorId} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <Link to={href} className="flex items-center gap-3 min-w-0">
                            {hero?.avatar ? (
                              <img src={hero.avatar} alt={hero.displayName} className="h-10 w-10 rounded-full" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-white/10" />
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="font-semibold truncate">{hero?.displayName || `Creator #${creatorId}`}</div>
                                <StatusPill status={r.sub.status} />
                              </div>
                              <div className="mt-0.5 text-xs text-white/70 truncate">{hero?.handle || ''}</div>
                            </div>
                          </Link>
                          <div className="flex items-center gap-2 shrink-0">
                            <Link
                              to={href}
                              className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10"
                            >
                              View
                            </Link>
                            <button
                              type="button"
                              disabled={busy === creatorId || !renewEligible || !renewalsEnabled}
                              onClick={() => doRenew(creatorId)}
                              className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                              title={
                                !renewalsEnabled
                                  ? 'Renewals are disabled by this creator.'
                                  : !renewEligible
                                    ? 'Renew is available within 7 days of expiry.'
                                    : 'Renew now (adds 30 days)'
                              }
                            >
                              {busy === creatorId ? 'Working…' : 'Renew'}
                            </button>
                            {tiers.length > 0 ? (
                              <button
                                type="button"
                                disabled={busy === creatorId}
                                onClick={() => {
                                  setTierModal({
                                    creatorId,
                                    creatorName: hero?.displayName || `Creator #${creatorId}`,
                                    currentRank: Number(r.sub.tier_rank || 1),
                                    tiers: tiers.map((t: any) => ({
                                      id: Number(t.id),
                                      name: String(t.name),
                                      rank: Number(t.rank),
                                      monthly_price_pixe: Number(t.monthly_price_pixe),
                                    })),
                                  })
                                  setTierPick(null)
                                }}
                                className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                              >
                                Change tier
                              </button>
                            ) : null}
                            <button
                              type="button"
                              disabled={busy === creatorId}
                              onClick={() => setConfirm({ creatorId, displayName: hero?.displayName || `Creator #${creatorId}` })}
                              className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-100 hover:bg-red-500/15 disabled:opacity-60"
                            >
                              {busy === creatorId ? 'Working…' : 'Cancel'}
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/65">
                          <div>
                            <span className="text-white/50">Price:</span>{' '}
                            <span className="text-white/80">{price ? `${price} PIXE/month` : '—'}</span>
                          </div>
                          {tierName ? (
                            <div>
                              <span className="text-white/50">Tier:</span>{' '}
                              <span className="text-white/80">{tierName}</span>
                            </div>
                          ) : null}
                          {periodEnd ? (
                            <div>
                              <span className="text-white/50">Renews:</span>{' '}
                              <span className="text-white/80">
                                {r.sub.renews === false ? 'No' : periodEnd}
                                {!renewalsEnabled ? ' (disabled)' : null}
                              </span>
                            </div>
                          ) : null}
                          {daysLeft !== null && daysLeft > 0 ? (
                            <div>
                              <span className="text-white/50">Expires in:</span>{' '}
                              <span className="text-white/80">{Math.ceil(daysLeft)}d</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </section>

            {inactive.length > 0 && (
              <section>
                <div className="text-sm font-semibold text-white/85">Past</div>
                <div className="mt-3 space-y-3">
                  {inactive.map((r) => {
                    const creatorId = r.sub.creator_id
                    const hero = r.people?.hero
                    const publicId = r.people?._meta?.public_id
                    const href = publicId ? `/people/i/${encodeURIComponent(publicId)}` : '/people'
                    const price = r.creatorSettings?.monthly_price_pixe
                    const renewalsEnabled = r.creatorSettings?.is_enabled !== false
                    const isCanceled = r.sub.status === 'canceled'
                    return (
                      <div key={creatorId} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <Link to={href} className="flex items-center gap-3 min-w-0">
                            {hero?.avatar ? (
                              <img src={hero.avatar} alt={hero.displayName} className="h-10 w-10 rounded-full" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-white/10" />
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="font-semibold truncate">{hero?.displayName || `Creator #${creatorId}`}</div>
                                <StatusPill status={r.sub.status} />
                              </div>
                              <div className="mt-0.5 text-xs text-white/70 truncate">{hero?.handle || ''}</div>
                            </div>
                          </Link>
                          <div className="flex items-center gap-2 shrink-0">
                            <Link
                              to={href}
                              className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10"
                            >
                              View
                            </Link>
                            <button
                              type="button"
                              disabled={busy === creatorId || (isCanceled && !renewalsEnabled)}
                              onClick={() => {
                                if (isCanceled) {
                                  void doRenew(creatorId)
                                } else {
                                  void doResubscribe(creatorId)
                                }
                              }}
                              className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                              title={isCanceled && !renewalsEnabled ? 'Renewals are disabled by this creator.' : undefined}
                            >
                              {busy === creatorId ? 'Working�' : isCanceled ? 'Renew' : 'Re-subscribe'}
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-white/65">
                          <span className="text-white/50">Price:</span>{' '}
                          <span className="text-white/80">{price ? `${price} PIXE/month` : '—'}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-950 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-lg font-bold">Cancel subscription?</div>
            <div className="mt-1 text-sm text-white/70">
              You’ll keep access until the end of your current period. You can re-subscribe anytime.
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/80">
              <div className="font-semibold">{confirm.displayName}</div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                onClick={() => setConfirm(null)}
              >
                Keep subscription
              </button>
              <button
                type="button"
                className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-100 hover:bg-red-500/15"
                onClick={async () => {
                  const id = confirm.creatorId
                  setConfirm(null)
                  await doCancel(id)
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {tierModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setTierModal(null)}>
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-950 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-lg font-bold">Change tier</div>
            <div className="mt-1 text-sm text-white/70">
              Upgrades apply immediately. Downgrades apply on your next renewal.
            </div>

            <div className="mt-4 space-y-2">
              {tierModal.tiers.map((t) => {
                const active = (tierPick ?? tierModal.currentRank) === t.rank
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTierPick(t.rank)}
                    className={[
                      'w-full text-left rounded-xl border p-3 transition',
                      active ? 'border-white/20 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-white/90">{t.name}</div>
                        <div className="mt-0.5 text-xs text-white/65">Tier {t.rank}+</div>
                      </div>
                      <div className="text-sm font-extrabold text-white">{t.monthly_price_pixe} PIXE</div>
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                onClick={() => setTierModal(null)}
              >
                Close
              </button>
              <button
                type="button"
                disabled={busy === tierModal.creatorId || tierPick === null || tierPick === tierModal.currentRank}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                onClick={async () => {
                  const targetRank = tierPick
                  if (targetRank === null) return
                  const target = tierModal.tiers.find((t) => t.rank === targetRank)
                  if (!target) return
                  const changeType: 'upgrade' | 'downgrade' = target.rank > tierModal.currentRank ? 'upgrade' : 'downgrade'
                  await doChangeTier(tierModal.creatorId, target.id, changeType)
                  setTierModal(null)
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


